/**
 * Experience Component
 * Displays professional experience in a timeline format
 *  CORRIGÉ : Numérotation de section (05) et structure simplifiée, statistiques supprimées
 */
import React, { useState, useEffect } from 'react';
import { vars } from '../../../../styles/vars.css';
import { useInView } from 'react-intersection-observer';
import * as styles from './experience.css';
import { commonClasses } from "../../../../styles/indexStyles";
import { useLanguage } from "../../../../hooks";
import { useTheme } from "../../../shared/context/indexSharedContext";
import { apiService } from '../../../shared/services/apiService';
import { Spinner } from '../../../admin/components/Spinner/Spinner';
import type { ExperienceItem, WorkExperience, EducationExperience } from "../../../../types";

// ================================
// COMPOSANT PRINCIPAL
// ================================

export const Experience: React.FC = () => {
  // ================================
  // HOOKS
  // ================================
  const { t, language } = useLanguage(); // Récupérer la langue courante
  const { theme } = useTheme();
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // ================================
  // ÉTAT LOCAL
  // ================================
  const [experiences, setExperiences] = useState<ExperienceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animate, setAnimate] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // ================================
  // FONCTIONS DE TRADUCTION
  // ================================
  const getExperienceDescription = (row: any): string => {
    // EN: afficher uniquement description_en si disponible; sinon message neutre EN
    if (language === 'en') {
      return row.description_en || 'Professional experience details coming soon.';
    }
    // FR: prioriser FR puis fallback générique FR
    return row.description_fr || row.description || `Expérience professionnelle chez ${row.company_name || 'cette entreprise'}`;
  };

  const getExperiencePosition = (position: string): string => {
    if (language === 'en') {
      const translations: { [key: string]: string } = {
        'Développeur web - Stage de validation de cursus': 'Web Developer - Validation Internship',
        'Découverte du quotidien / métier': 'Daily Life / Job Discovery',
        "Responsable d'agences": 'Branch Manager'
      };
      return translations[position] || position;
    }
    return position;
  };

  const translateArray = (items: string[]): string[] => {
    if (language === 'en') {
      const translations: { [key: string]: string } = {
        // Technologies
        'Management': 'Management',
        'Organisation': 'Organization', 
        'Communication': 'Communication',
        'Gestion d\'équipe': 'Team Management',
        // Réalisations
        'Management d\'équipe': 'Team Management',
        'Qualité de service': 'Service Quality',
        'Résolution de problèmes': 'Problem Solving',
        'Autonomie': 'Autonomy',
        'Travail d\'équipe': 'Teamwork',
        'Découverte métier': 'Career Discovery',
        'Langages web': 'Web Languages',
        'Projets pratiques': 'Practical Projects',
        'Site e-commerce': 'E-commerce Website',
        'Application de gestion': 'Management Application',
        'Optimisation SEO': 'SEO Optimization',
        'Performances web': 'Web Performance'
      };
      return items.map(item => translations[item] || item);
    }
    return items;
  };

  // ================================
  // UTILITAIRES POUR ADAPTATION THÈME
  // ================================
  const getThemeColors = () => {
    const isLight = theme === 'light';
    return {
      cardBg: isLight ? '#f8f9fa' : '#112240',  // Utilise #112240 en mode sombre et #f8f9fa en mode light
      cardBorder: isLight ? '#d1d5db' : 'rgba(100, 255, 218, 0.1)',
      text: isLight ? '#1a1a1a' : '#ccd6f6',
      textSecondary: isLight ? '#6b7280' : '#8892b0',
      primary: isLight ? '#0066cc' : '#64ffda',
      bgAccent: isLight ? 'rgba(0, 102, 204, 0.08)' : 'rgba(100, 255, 218, 0.05)',
      borderAccent: isLight ? 'rgba(0, 102, 204, 0.2)' : 'rgba(100, 255, 218, 0.1)',
    };
  };

  // ================================
  // CHARGEMENT DES DONNÉES
  // ================================
  // ✅ CORRECTION - Transformer en useCallback avec dépendance [language]
  const loadExperiences = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ PROTECTION ANTI-BOUCLE - Utilise apiService avec cache 3 minutes
      const response = await apiService.get<ExperienceItem[]>('/experiences', true, {
        cacheTtlMs: 180000, // 3 minutes cache
        useCache: true
      });

      if (response?.data) {
        const responseData = response.data;
        let experiencesArray: any[] = [];

        if (Array.isArray(responseData)) {
          experiencesArray = responseData;
        } else if (responseData && typeof responseData === 'object') {
          experiencesArray = (responseData as any)?.experiences ||
            (responseData as any)?.data ||
            (responseData as any)?.items ||
            ((responseData as any)?.id ? [responseData] : []);
        } else {
          experiencesArray = [];
        }

        if (experiencesArray.length > 0) {
          const experiencesData: ExperienceItem[] = experiencesArray.map((row: any, index: number) => {
            const baseExperience = {
              id: String(row.id || `exp-${Date.now()}-${index}`),
              title: getExperiencePosition(String(row.position || row.title || 'Poste')),
              company: String(row.company_name || row.company || 'Entreprise'),
              location: String(row.location || 'Non spécifié'),
              startDate: String(row.start_date || row.startDate || new Date().toISOString()),
              endDate: row.end_date ? String(row.end_date) : (row.is_current ? undefined : row.endDate),
              description: getExperienceDescription(row),
              is_visible: Boolean(row.is_visible ?? true),
              order_index: Number(row.order_index ?? index),
              created_at: String(row.created_at || new Date().toISOString()),
              updated_at: String(row.updated_at || new Date().toISOString()),
            };

            // Déterminer le type d'expérience basé sur les données disponibles
            const isEducation = row.degree || row.field || row.institution;
            
            if (isEducation) {
              return {
                ...baseExperience,
                type: 'education' as const,
                degree: String(row.degree || row.title || 'Diplôme'),
                field: String(row.field || row.position || 'Domaine'),
                gpa: row.gpa ? Number(row.gpa) : undefined,
                honors: row.honors ? translateArray(row.honors.split('\n').filter((h: string) => h.trim())) : [],
                courses: row.courses ? translateArray(row.courses.split(',').map((c: string) => c.trim())) : [],
              } as EducationExperience;
            } else {
              return {
                ...baseExperience,
                type: 'work' as const,
                position: getExperiencePosition(String(row.position || row.title || 'Poste')),
                achievements: row.achievements ? translateArray(row.achievements.split('\n').filter((r: string) => r.trim())) : [],
                technologies: row.technologies ? translateArray(row.technologies.split(',').map((t: string) => t.trim())) : [],
              } as WorkExperience;
            }
          });

          const visibleExperiences = experiencesData
            .filter(exp => exp.is_visible)
            .sort((a, b) => a.order_index - b.order_index);

          setExperiences(visibleExperiences);
          setRetryCount(0);
        } else {
          setExperiences([]);
        }
      } else {
        setExperiences([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur de chargement des expériences';
      setError(errorMessage);
      setExperiences([]);
    } finally {
      setLoading(false);
    }
  }, [language]);

  // ✅ CORRECTION - Un seul useEffect pour charger les données
  useEffect(() => {
    loadExperiences();
  }, [loadExperiences]);

  // ================================
  // ANIMATION AU SCROLL
  // ================================
  useEffect(() => {
    if (inView && !animate) {
      const timer = setTimeout(() => setAnimate(true), 200);
      return () => clearTimeout(timer);
    }
  }, [inView, animate]);

  // ================================
  // UTILITAIRES
  // ================================
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', {
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const calculateDuration = (start: string, end?: string | null): string => {
    if (!start) return '';
    try {
      const startDate = new Date(start);
      const endDate = end ? new Date(end) : new Date();
      const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth());
      if (months < 1) return '1 mois';
      if (months < 12) return `${months} mois`;
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      if (remainingMonths === 0) {
        return years === 1 ? '1 an' : `${years} ans`;
      }
      return `${years} an${years > 1 ? 's' : ''} ${remainingMonths} mois`;
    } catch {
      return '';
    }
  };

  const getExperienceIcon = (type: string): string => {
    switch (type) {
      case 'work': return '💼';
      case 'education': return '🎓';
      case 'internship': return '🎓';
      case 'freelance': return '🚀';
      default: return '💼';
    }
  };

  const retryLoad = () => {
    if (retryCount < 2) {
      setRetryCount(prev => prev + 1);
      loadExperiences();
    }
  };

  // ================================
  // RENDU CONDITIONNEL - CHARGEMENT
  // ================================
  if (loading) {
    return (
      <section id="experience" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={commonClasses.highlight} aria-hidden="true">05.</span>
            {t('experience.title') || 'Expérience'}
          </h2>
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#8892b0'
          }}>
            <Spinner size="large" ariaLabel="Chargement des expériences" />
          </div>
        </div>
      </section>
    );
  }

  // ================================
  // RENDU CONDITIONNEL - ERREUR
  // ================================
  if (error && experiences.length === 0) {
    return (
      <section id="experience" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={commonClasses.highlight} aria-hidden="true">05.</span>
            {t('experience.title') || 'Expérience'}
          </h2>
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: vars.color.textSecondary,
            backgroundColor: vars.color.dangerSurface,
            borderRadius: '8px',
            border: `1px solid ${vars.color.dangerBorder}`
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: vars.color.dangerText }}>
              ❌ Erreur de chargement
            </h3>
            <p style={{ marginBottom: '1rem' }}>{error}</p>
            <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
              Tentatives : {retryCount}/2
            </p>
            <button
              onClick={retryLoad}
              disabled={retryCount >= 2}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: retryCount >= 2 ? '#666' : '#64ffda',
                color: retryCount >= 2 ? '#ccc' : '#0a192f',
                border: 'none',
                borderRadius: '4px',
                cursor: retryCount >= 2 ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
            >
              {retryCount >= 2 ? 'Limite atteinte' : (t('common.retry') || 'Réessayer')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ================================
  // RENDU CONDITIONNEL - AUCUNE DONNÉE
  // ================================
  if (experiences.length === 0) {
    const colors = getThemeColors();
    return (
      <section id="experience" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={commonClasses.highlight} aria-hidden="true">05.</span>
            {t('experience.title') || 'Expérience'}
          </h2>
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: colors.textSecondary,
            backgroundColor: colors.bgAccent,
            borderRadius: '8px',
            border: `1px solid ${colors.borderAccent}`
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: colors.text }}>
              💼 Expériences à venir
            </h3>
            <p style={{ fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
              Les informations sur mon expérience professionnelle seront bientôt disponibles.
            </p>
            <button
              onClick={() => {
                setRetryCount(0);
                loadExperiences();
              }}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: colors.primary,
                color: theme === 'light' ? '#ffffff' : '#0a192f',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
            >
              {t('common.retry') || 'Réessayer'}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ================================
  // RENDU PRINCIPAL
  // ================================
  const colors = getThemeColors();
  
  return (
    <section
      id="experience"
      className={`${commonClasses.section} ${styles.section}`}
      ref={ref}
      aria-labelledby="experience-heading"
    >
      <div className={styles.container}>
        {/* ✅ TITRE AVEC NUMÉROTATION CORRIGÉE */}
        <h2
          id="experience-heading"
          className={`${commonClasses.title} ${styles.title}`}
        >
          <span className={commonClasses.highlight} aria-hidden="true">05.</span>
          <span data-translate="true">
            {t('experience.title') || 'Expérience'}
          </span>
        </h2>

        {/* ✅ TIMELINE DES EXPÉRIENCES */}
        <div className={styles.timeline || 'timeline'}>
          <div className={styles.timelineSection || 'timeline-section'}>
            <h3 className={styles.sectionTitle || 'section-title'} style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: colors.primary,
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              💼 {language === 'en' ? 'Professional Experience' : 'Expérience Professionnelle'}
            </h3>

            {experiences.map((experience, index) => (
              <div
                key={experience.id}
                className={styles.timelineItem || 'timeline-item'}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  opacity: animate ? 1 : 0,
                  transform: animate ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease-out ${index * 0.1}s, transform 0.6s ease-out ${index * 0.1}s`,
                  marginBottom: '2rem',
                  padding: '1.5rem',
                  backgroundColor: colors.cardBg,
                  borderRadius: '8px',
                  border: `1px solid ${colors.cardBorder}`,
                  position: 'relative'
                }}
              >
                {/* Timeline dot */}
                <div style={{
                  position: 'absolute',
                  left: '-10px',
                  top: '1.5rem',
                  width: '20px',
                  height: '20px',
                  backgroundColor: colors.primary,
                  borderRadius: '50%',
                  border: `3px solid ${theme === 'light' ? '#ffffff' : '#0a192f'}`
                }}></div>

                <div className={styles.timelineContent || 'timeline-content'}>
                  <div className={styles.experienceHeader || 'experience-header'}>
                    <h4 className={styles.experienceTitle || 'experience-title'} style={{
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      color: colors.text,
                      marginBottom: '0.5rem'
                    }}>
                      {getExperienceIcon(experience.type)} {experience.type === 'work' ? (experience as WorkExperience).position : experience.title}
                    </h4>
                    <div className={styles.experienceCompany || 'experience-company'} style={{
                      fontSize: '1.1rem',
                      color: colors.primary,
                      marginBottom: '0.5rem',
                      fontWeight: 500
                    }}>
                      {experience.company} • {experience.location}
                    </div>
                    <div className={styles.experienceDuration || 'experience-duration'} style={{
                      fontSize: '0.9rem',
                      color: colors.textSecondary,
                      marginBottom: '1rem'
                    }}>
                      {formatDate(experience.startDate)} - {
                        experience.endDate ? formatDate(experience.endDate) : (language === 'en' ? 'Present' : 'Présent')
                      }
                      <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>
                        ({calculateDuration(experience.startDate, experience.endDate)})
                      </span>
                    </div>
                  </div>

                  <div className={styles.experienceDescription || 'experience-description'} style={{
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: colors.textSecondary,
                    marginBottom: '1rem'
                  }}>
                    {experience.description}
                  </div>

                  {/* ACHIEVEMENTS */}
                  {experience.type === 'work' && (experience as WorkExperience).achievements && (experience as WorkExperience).achievements.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h5 style={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: colors.text,
                          marginBottom: '0.5rem'
                        }}>
                          {language === 'en' ? '🎯 Key achievements' : '🎯 Réalisations principales'}
                        </h5>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0
                        }}>
                          {(experience as WorkExperience).achievements.map((achievement: string, achIndex: number) => (
                            <li key={achIndex} style={{
                              fontSize: '0.9rem',
                              color: colors.textSecondary,
                              marginBottom: '0.25rem',
                              paddingLeft: '1rem',
                              position: 'relative'
                            }}>
                              <span style={{
                                position: 'absolute',
                                left: '0',
                                color: colors.primary
                              }}>•</span>
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* ✅ AFFICHAGE CONDITIONNEL DES TECHNOLOGIES */}
                  {experience.type === 'work' && (experience as WorkExperience).technologies && (experience as WorkExperience).technologies!.length > 0 && (
                      <div className={styles.experienceTechnologies || 'experience-technologies'} style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        marginTop: '1rem'
                      }}>
                        {(experience as WorkExperience).technologies!.map((tech: string, techIndex: number) => (
                          <span
                            key={techIndex}
                            className={styles.technologyTag}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ✅ EXPORT DEFAULT
export default Experience;